import {uniqueId} from 'lodash';
import {useCallback} from 'react';
import {idFromGlobalId} from '../../forecast-app/shared/util/GlobalIdUtil';
import Util from '../../forecast-app/shared/util/util';
import {
	BaselineItem,
	Company,
	Deliverable,
	ExpenseRow,
	GroupedBaselineTotals,
	Phase,
	ProjectPhaseDeliverable,
	RoleRow,
} from './BaselineTypes';

const renormaliseRoleRevenues = (updatedRows: BaselineItem[], multiplier: number) =>
	updatedRows.map(row => {
		if ('phaseBaselineRoleId' in row) {
			const roleRow = row as RoleRow;
			const revenue = roleRow.valueOfService * multiplier;
			const profit = revenue - roleRow.cost;
			const margin = revenue ? Util.roundFloatToTwoDecimals(profit / revenue) : 0;
			return {...roleRow, revenue, profit, margin};
		}
		return row;
	});

type UseBaselineLogicArgs = {
	setProjectBaselineItems?: React.Dispatch<React.SetStateAction<BaselineItem[]>>;
	projectBudget?: number;
	isFixedPrice?: boolean;
	groupedProjectTotals?: GroupedBaselineTotals;
	phase: Phase;
	company: Company;
	deliverableId?: string;
};

export function useBaselineLogic({
	setProjectBaselineItems,
	projectBudget,
	isFixedPrice,
	groupedProjectTotals,
	phase,
	company,
	deliverableId,
}: UseBaselineLogicArgs) {
	const emptyRole = (): RoleRow => ({
		phaseBaselineRoleId: uniqueId(),
		projectId: phase?.projectId || '',
		phaseId: phase?.id || '',
		deliverableId,
		roleName: '',
		roleId: '',
		hours: 0,
		valueOfService: 0,
		revenue: 0,
		cost: 0,
		profit: 0,
		ratePerHour: 0,
		costPerHour: 0,
	});

	const emptyExpense = (): ExpenseRow => ({
		phaseBaselineExpenseId: uniqueId(),
		projectId: phase?.projectId || '',
		phaseId: phase?.id || '',
		deliverableId,
		name: '',
		categoryName: '',
		categoryId: '',
		revenue: 0,
		cost: 0,
		markup: 0,
		profit: 0,
		margin: 0,
	});

	const recalculateRoleRows = useCallback(
		(row: RoleRow) => {
			const {hours, costPerHour, ratePerHour} = row;

			const valueOfService = ratePerHour * hours;
			const cost = costPerHour * hours;
			const revenue = valueOfService;
			const profit = revenue - cost;

			const updateFields = {valueOfService, cost, profit, revenue};

			const newRow = {...row, ...updateFields};

			if (setProjectBaselineItems) {
				setProjectBaselineItems(old => {
					const rowIndex = old.findIndex(
						item => 'phaseBaselineRoleId' in item && item.phaseBaselineRoleId === row.phaseBaselineRoleId
					);

					const updatedRows = [...old.slice(0, rowIndex), newRow, ...old.slice(rowIndex + 1)];

					if (projectBudget !== undefined && isFixedPrice) {
						const oldRow = old[rowIndex] as RoleRow;

						const valueOfServiceDiff = valueOfService - oldRow.valueOfService;

						const newTotalRolesValueOfService =
							(groupedProjectTotals?.rolesTotals.baselineTimeAndExpenses || 0) + valueOfServiceDiff;

						const multiplier = newTotalRolesValueOfService
							? (projectBudget - (groupedProjectTotals?.expensesTotals.baselineRevenue || 0)) /
							  newTotalRolesValueOfService
							: 1;

						return renormaliseRoleRevenues(updatedRows, multiplier);
					}
					return updatedRows;
				});
			}
		},
		[setProjectBaselineItems, projectBudget, isFixedPrice, groupedProjectTotals, renormaliseRoleRevenues]
	);

	const onRoleSelect = useCallback(
		(roleId: string, row: RoleRow) => {
			const convertedRoleId = idFromGlobalId(roleId);
			const averageRate =
				phase && phase.averageRatesByRole
					? phase?.averageRatesByRole.find(rate => rate?.roleId === convertedRoleId)
					: null;

			const role = company?.roles?.edges?.find(edge => edge?.node?.id === roleId);
			if (role && role.node) {
				const newRow = {...row};
				newRow.roleId = role.node.id;
				newRow.roleName = role.node.name || '';
				newRow.ratePerHour = averageRate && averageRate.rate ? averageRate.rate : 0;
				newRow.costPerHour = averageRate && averageRate.costRate ? averageRate.costRate : 0;
				recalculateRoleRows(newRow);
			}
		},
		[company, phase, idFromGlobalId, recalculateRoleRows]
	);

	const onRoleAdd = useCallback(() => {
		if (setProjectBaselineItems) {
			setProjectBaselineItems(old => [...old, emptyRole()]);
		}
	}, [setProjectBaselineItems]);

	const onDeleteRoleRow = useCallback(
		(row: RoleRow) => {
			if (setProjectBaselineItems) {
				setProjectBaselineItems(old => {
					const rowIndex = old.findIndex(
						item => 'phaseBaselineRoleId' in item && item.phaseBaselineRoleId === row.phaseBaselineRoleId
					);

					const updatedRows = [...old.slice(0, rowIndex), ...old.slice(rowIndex + 1)];

					if (projectBudget !== undefined && isFixedPrice) {
						const oldRow = old[rowIndex] as RoleRow;

						const totalRolesValueOfService =
							(groupedProjectTotals?.rolesTotals.baselineTimeAndExpenses || 0) - oldRow.valueOfService;

						const multiplier = totalRolesValueOfService
							? (projectBudget - (groupedProjectTotals?.expensesTotals.baselineRevenue || 0)) /
							  totalRolesValueOfService
							: 1;

						return renormaliseRoleRevenues(updatedRows, multiplier);
					}

					return updatedRows;
				});
			}
		},
		[setProjectBaselineItems, projectBudget, isFixedPrice, groupedProjectTotals, renormaliseRoleRevenues]
	);

	const recalculateExpenseRows = useCallback(
		(row: ExpenseRow, changedField: string) => {
			let revenue, cost, markup;
			if (changedField === 'revenue' || changedField === 'cost') {
				revenue = row.revenue;
				cost = row.cost;
				markup = cost ? (revenue - cost) / cost : 0;
			} else if (changedField === 'markup') {
				markup = row.markup;
				const markupMultiplier = 1 + markup;
				if (row.revenue) {
					revenue = row.revenue;
					cost = markupMultiplier !== 0 ? revenue / markupMultiplier : 0;
				} else {
					cost = row.cost;
					revenue = cost * markupMultiplier;
				}
			} else {
				cost = row.cost;
				markup = row.markup;
				revenue = cost + markup * cost;
			}

			const profit = revenue - cost;
			const margin = revenue ? profit / revenue : 0;

			const updateFields = {profit, margin, markup, revenue, cost};

			const newRow = {...row, ...updateFields};

			if (setProjectBaselineItems) {
				setProjectBaselineItems(old => {
					const rowIndex = old.findIndex(
						item => 'phaseBaselineExpenseId' in item && item.phaseBaselineExpenseId === row.phaseBaselineExpenseId
					);
					const updatedRows = [...old.slice(0, rowIndex), newRow, ...old.slice(rowIndex + 1)];

					if (projectBudget !== undefined && isFixedPrice) {
						const oldRow = old[rowIndex] as ExpenseRow;

						const revenueDiff = revenue - oldRow.revenue;
						const newTotalExpensesRevenue =
							(groupedProjectTotals?.expensesTotals.baselineRevenue || 0) + revenueDiff;

						const multiplier = groupedProjectTotals?.rolesTotals.baselineTimeAndExpenses
							? (projectBudget - newTotalExpensesRevenue) /
							  groupedProjectTotals?.rolesTotals.baselineTimeAndExpenses
							: 1;

						return renormaliseRoleRevenues(updatedRows, multiplier);
					}
					return updatedRows;
				});
			}
		},
		[setProjectBaselineItems, projectBudget, isFixedPrice, groupedProjectTotals, renormaliseRoleRevenues]
	);

	const onExpenseCategorySelect = useCallback(
		(categoryId: string, row: ExpenseRow) => {
			const category = company?.expenseCategories?.edges?.find(edge => edge?.node?.id === categoryId);

			if (category && category.node && category.node.name) {
				const newRow = {
					...row,
					categoryId,
					categoryName: category.node.name,
				};

				if (setProjectBaselineItems) {
					setProjectBaselineItems(old => {
						const rowIndex = old.findIndex(
							item =>
								'phaseBaselineExpenseId' in item && item.phaseBaselineExpenseId === row.phaseBaselineExpenseId
						);
						return [...old.slice(0, rowIndex), newRow, ...old.slice(rowIndex + 1)];
					});
				}
			}
		},
		[company, setProjectBaselineItems]
	);

	const onExpenseAdd = useCallback(() => {
		if (setProjectBaselineItems) {
			setProjectBaselineItems(old => [...old, emptyExpense()]);
		}
	}, [setProjectBaselineItems]);

	const onDeleteExpenseRow = useCallback(
		(row: ExpenseRow) => {
			if (setProjectBaselineItems) {
				setProjectBaselineItems(old => {
					const rowIndex = old.findIndex(
						item => 'phaseBaselineExpenseId' in item && item.phaseBaselineExpenseId === row.phaseBaselineExpenseId
					);
					const updatedRows = [...old.slice(0, rowIndex), ...old.slice(rowIndex + 1)];

					if (projectBudget !== undefined && isFixedPrice) {
						const oldRow = old[rowIndex] as ExpenseRow;

						const newTotalExpensesRevenue =
							(groupedProjectTotals?.expensesTotals.baselineRevenue || 0) - oldRow.revenue;

						const multiplier = groupedProjectTotals?.rolesTotals.baselineTimeAndExpenses
							? (projectBudget - newTotalExpensesRevenue) /
							  groupedProjectTotals?.rolesTotals.baselineTimeAndExpenses
							: 1;

						return renormaliseRoleRevenues(updatedRows, multiplier);
					}

					return updatedRows;
				});
			}
		},
		[setProjectBaselineItems, projectBudget, isFixedPrice, groupedProjectTotals, renormaliseRoleRevenues]
	);

	return {
		recalculateRoleRows,
		onRoleSelect,
		onRoleAdd,
		onDeleteRoleRow,
		recalculateExpenseRows,
		onExpenseCategorySelect,
		onExpenseAdd,
		onDeleteExpenseRow,
		emptyRole,
		emptyExpense,
	};
}

type UseDeliverableLogicArgs = {
	setProjectBaselineItems?: React.Dispatch<React.SetStateAction<BaselineItem[]>>;
	setPhaseDeliverables?: React.Dispatch<React.SetStateAction<ProjectPhaseDeliverable[]>>;
	phaseDeliverable?: ProjectPhaseDeliverable;
	phaseId: string;
};

export function useDeliverableLogic({
	setProjectBaselineItems,
	setPhaseDeliverables,
	phaseDeliverable,
	phaseId: currentPhaseId,
}: UseDeliverableLogicArgs) {
	const currentDeliverable = phaseDeliverable?.deliverable;

	const handleChangeDeliverable = useCallback(
		(newDeliverable: Deliverable) => {
			if (setPhaseDeliverables) {
				setPhaseDeliverables(old => {
					const prevIndex = old.findIndex(item => phaseDeliverable?.id && item.id === phaseDeliverable?.id);

					const newPhaseDeliverableAlreadyExists = old.find(
						phaseDeliverable =>
							phaseDeliverable?.deliverable?.id === newDeliverable.id &&
							phaseDeliverable?.phaseId === currentPhaseId
					);

					// Changing from one deliverable to another
					if (prevIndex > 0) {
						if (newPhaseDeliverableAlreadyExists) {
							return [...old.slice(0, prevIndex), ...old.slice(prevIndex + 1)];
						}

						// Update the deliverable by replacing the old one with the new one
						const updatedPhaseDeliverable: ProjectPhaseDeliverable = {
							...old[prevIndex],
							deliverable: newDeliverable,
						};

						return [...old.slice(0, prevIndex), updatedPhaseDeliverable, ...old.slice(prevIndex + 1)];
					}
					// Changing from no deliverable to a deliverable
					else {
						if (newPhaseDeliverableAlreadyExists) {
							return old;
						}

						// Add new deliverable to the phase
						const newPhaseDeliverable: ProjectPhaseDeliverable = {
							id: uniqueId(),
							phaseId: currentPhaseId,
							deliverable: newDeliverable,
							financialNumbers: {
								baselineMinutes: 0,
								baselineTimeAndExpenses: 0,
								baselineRevenue: 0,
								baselineCost: 0,
								baselineProfit: 0,
								baselineMargin: 0,
							},
						};

						return [...old, newPhaseDeliverable];
					}
				});
			}
			if (setProjectBaselineItems) {
				setProjectBaselineItems(old => {
					return old.map(baselineItem => {
						const {deliverableId, phaseId} = baselineItem;

						if (phaseId !== currentPhaseId || (deliverableId && deliverableId !== currentDeliverable?.id)) {
							return baselineItem;
						}

						return {...baselineItem, deliverableId: newDeliverable.id};
					});
				});
			}
		},
		[setPhaseDeliverables, setProjectBaselineItems, phaseDeliverable]
	);

	const handleDeleteDeliverable = useCallback(() => {
		setPhaseDeliverables &&
			setPhaseDeliverables(old => {
				const prevIndex = old.findIndex(item => item.id === phaseDeliverable?.id);
				return [...old.slice(0, prevIndex), ...old.slice(prevIndex + 1)];
			});
		setProjectBaselineItems &&
			setProjectBaselineItems(old => {
				return old.filter(baselineItem => {
					const {deliverableId, phaseId} = baselineItem;

					if (phaseId !== currentPhaseId || deliverableId === undefined) {
						return true;
					}

					return deliverableId !== currentDeliverable?.id;
				});
			});
	}, [setPhaseDeliverables, setProjectBaselineItems, phaseDeliverable]);

	return {
		handleChangeDeliverable,
		handleDeleteDeliverable,
	};
}
